import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { totpSchema, type TotpFormData } from '@/lib/validation';
import { authApi, ApiError } from '@/lib/pillarApi';
import { useAuthStore } from '@/store/auth';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { runInBackground } from '@/lib/utils';
import {
    SessionUserProfileDetailedResponseSchema,
    VerifyTotpResponseSchema,
} from '@pillar-portal/api-types';

export default function TotpPage() {
    const log = createComponentLogger('TotpPage');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnToPayment: boolean = searchParams.get('return_to_payment') === '1';
    const paymentRequestUuid: string = searchParams.get('payment_request_uuid')?.trim() ?? '';
    const queryClient = useQueryClient();
    const [error, setError] = useState<string>('');
    const [isRedirecting, setIsRedirecting] = useState(false);
    const setUser = useAuthStore((state) => state.setUser);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<TotpFormData>({
        resolver: zodResolver(totpSchema),
    });

    const totpMutation = useMutation({
        mutationFn: async (data: TotpFormData) => {
            const response = await authApi.verifyTotp(data);
            return VerifyTotpResponseSchema.parse(response);
        },
        onSuccess: async () => {
            // Clear any stale cache from previous user session
            queryClient.clear();
            // Clear Zustand store first to ensure fresh state
            useAuthStore.getState().logout();
            setIsRedirecting(true);
            // Fetch full profile to get complete user data including organizations
            try {
                const profileResponse = await authApi.getProfile();
                if (profileResponse) {
                    const profileData =
                        SessionUserProfileDetailedResponseSchema.parse(profileResponse);
                    setUser(profileData);
                }
                window.location.assign(returnToPayment ? '/pay' : '/dashboard');
            } catch (err) {
                // If profile fetch fails, still redirect and let ProtectedRoute handle it
                // This ensures we don't get stuck on TOTP page
                log.error('Failed to fetch profile after TOTP verification', err);
                window.location.assign(returnToPayment ? '/pay' : '/dashboard');
            }
        },
        onError: (err: Error) => {
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
            log.error('Error verifying TOTP', err);
        },
    });

    const onSubmit = (data: TotpFormData) => {
        setError('');
        totpMutation.mutate(data);
    };

    // Show loading screen during redirect to prevent flicker
    if (isRedirecting) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                            <p className="text-sm text-muted-foreground">Signing you in...</p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-3xl font-bold text-center">
                        Two-Factor Authentication
                    </CardTitle>
                    <CardDescription className="text-center">
                        Enter the 6-digit code from your authenticator app
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="totp_code">Verification Code</Label>
                            <Input
                                id="totp_code"
                                type="text"
                                placeholder="000000"
                                maxLength={6}
                                className="text-center text-2xl tracking-widest"
                                {...register('totp_code')}
                                disabled={totpMutation.isPending}
                                autoComplete="off"
                            />
                            {errors.totp_code && (
                                <p className="text-sm text-destructive">
                                    {errors.totp_code.message}
                                </p>
                            )}
                        </div>

                        {error && (
                            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                                {error}
                            </div>
                        )}

                        <Button type="submit" className="w-full" disabled={totpMutation.isPending}>
                            {totpMutation.isPending ? 'Verifying...' : 'Verify'}
                        </Button>

                        <Button
                            type="button"
                            variant="ghost"
                            className="w-full"
                            onClick={() => {
                                const nextSearchParams = new URLSearchParams();
                                if (paymentRequestUuid.length > 0) {
                                    nextSearchParams.set(
                                        'payment_request_uuid',
                                        paymentRequestUuid
                                    );
                                }
                                if (returnToPayment) {
                                    nextSearchParams.set('return_to_payment', '1');
                                }
                                const nextLoginPath =
                                    nextSearchParams.toString().length > 0
                                        ? `/login?${nextSearchParams.toString()}`
                                        : '/login';
                                runInBackground(navigate(nextLoginPath), (err) => {
                                    log.error('Error navigating back to login', err);
                                });
                            }}
                        >
                            Back to Login
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
